import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { useNavigate } from 'react-router';
import useNotifications from '../../hooks/useNotifications/useNotifications';
import { useCallback, useEffect, useMemo, useState } from 'react';
import PageContainer from '../layout/PageContainer';
import type { OverheadCostLineItem } from '../../models/OverheadCostLineItem';
import OverheadCostForm from './OverheadCostForm';
import { GET_OVERHEAD_COST_LINE_ITEMS_QUERY } from '../../queries/OverheadCostLineItemQueries';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import { UPDATE_OVERHEAD_COST_LINE_ITEMS_MUTATION } from '../../mutations/OverheadCostLineItemMutations';

function OverheadCostEditForm({
  initialItems,
  onSubmit
}: {
  initialItems: OverheadCostLineItem[];
  onSubmit: (overheadCostLineItems: OverheadCostLineItem[]) => Promise<boolean>;
}) {
  const navigate = useNavigate();

  const notifications = useNotifications();

  const handleFormSubmit = useCallback(async (overheadCostLineItems: OverheadCostLineItem[]) => {
    try {
      const success = await onSubmit(
        overheadCostLineItems
      );

      if (success) {
        notifications.show('Overhead costs edited successfully.', {
          severity: 'success',
          autoHideDuration: 3000,
        });

        navigate('/dashboard/overhead-costs');
      } else {
        notifications.show(
          'Failed to edit overhead costs. Reason: Unknown.',
          {
            severity: 'error',
            autoHideDuration: 3000,
          },
        );
      }
    } catch (editError) {
      notifications.show(
        `Failed to edit overhead costs. Reason: ${(editError as Error).message}`,
        {
          severity: 'error',
          autoHideDuration: 3000,
        },
      );
      throw editError;
    }
  }, [navigate, notifications, onSubmit]);

  return (
    <OverheadCostForm
      initialItems={initialItems}
      onSubmit={handleFormSubmit}
    />
  );
}

export default function OverheadCostEdit() {
  const [overheadCostLineItems, setOverheadCostLineItems] = useState<OverheadCostLineItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const [getOverheadCostLineItems] = useLazyQuery(
    GET_OVERHEAD_COST_LINE_ITEMS_QUERY);

  const [updateOverheadCostLineItems] = useMutation(
    UPDATE_OVERHEAD_COST_LINE_ITEMS_MUTATION, {
    refetchQueries: [{ query: GET_OVERHEAD_COST_LINE_ITEMS_QUERY }],
  });

  const loadData = useCallback(async () => {
    setError(null);
    setIsLoading(true);

    try {
      const { data, error: getOverheadCostLineItemsError } = await getOverheadCostLineItems();

      setIsLoading(false);
      setError(null);

      if (getOverheadCostLineItemsError) {
        setError(getOverheadCostLineItemsError);
      }

      if (data?.overheadCostLineItems) {
        setOverheadCostLineItems(data.overheadCostLineItems);
      } else {
        setError({ message: 'No overhead costs line items found' } as Error);
      }

    } catch (showDataError) {
      setIsLoading(false);
      setError(showDataError as Error);
    }
    setIsLoading(false);
  }, [getOverheadCostLineItems]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSubmit = useCallback(
    async (items: OverheadCostLineItem[]) => {
      setIsLoading(true);

      const { data: result } = await updateOverheadCostLineItems({
        variables: {
          overheadCostLineItemModels: items.map((item) => ({
            id: item.id,
            description: item.description,
            totalCost: item.totalCost,
            isActive: item.isActive,
            annualCostDivisor: item.annualCostDivisor,
            annualCostMultiplier: item.annualCostMultiplier,
            dateTimePurchased: item.dateTimePurchased,
            dateTimeDue: item.dateTimeDue,
            dateTimePaid: item.dateTimePaid,
            recipient: item.recipient,
            notes: item.notes
          })),
        },
      })

      setIsLoading(false);

      return result?.updated === true;
    },
    [updateOverheadCostLineItems],
  );

  const renderEdit = useMemo(() => {
    if (isLoading) {
      return (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            m: 1,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }
    if (error) {
      return (
        <Box sx={{ flexGrow: 1 }}>
          <Alert severity="error">{error.message}</Alert>
        </Box>
      );
    }

    return <OverheadCostEditForm
      initialItems={overheadCostLineItems}
      onSubmit={handleSubmit} />;
  }, [isLoading, error, overheadCostLineItems, handleSubmit]);

  return (
    <PageContainer
      title={'Edit Overhead Costs'}
      breadcrumbs={[
        { title: 'Edit' },
      ]}
    >
      <Box sx={{ display: 'flex', flex: 1 }}>{renderEdit}</Box>
    </PageContainer>
  );
}
